import { Eye, EyeOff, GripVertical, Plus, RefreshCw } from "lucide-react";
import { useMemo, useState, type DragEvent } from "react";
import type { Column, ColumnFilterType, FilterOperator, TableFilter } from "./data-table-types";
import { RightSidebarPanel } from "./RightSidebarPanel";
import { Button, SearchSelect } from "./ui";

function filterId(): string {
  return crypto.randomUUID();
}

export function operatorsForType(type: ColumnFilterType): { value: FilterOperator; label: string }[] {
  switch (type) {
    case "number":
      return [
        { value: "equals", label: "Equals" },
        { value: "gt", label: "Greater than" },
        { value: "lt", label: "Less than" },
        { value: "is_empty", label: "Is empty" },
        { value: "is_not_empty", label: "Is not empty" },
      ];
    case "boolean":
      return [
        { value: "equals", label: "Equals" },
        { value: "is_empty", label: "Is empty" },
        { value: "is_not_empty", label: "Is not empty" },
      ];
    case "select":
      return [
        { value: "equals", label: "Equals" },
        { value: "not_equals", label: "Does not equal" },
        { value: "is_empty", label: "Is empty" },
        { value: "is_not_empty", label: "Is not empty" },
      ];
    case "datetime":
      return [
        { value: "equals", label: "Equals" },
        { value: "gt", label: "After" },
        { value: "lt", label: "Before" },
        { value: "contains", label: "Contains" },
        { value: "is_empty", label: "Is empty" },
        { value: "is_not_empty", label: "Is not empty" },
      ];
    default:
      return [
        { value: "contains", label: "Contains" },
        { value: "equals", label: "Equals" },
        { value: "not_equals", label: "Does not equal" },
        { value: "is_empty", label: "Is empty" },
        { value: "is_not_empty", label: "Is not empty" },
      ];
  }
}

function needsValue(operator: FilterOperator): boolean {
  return operator !== "is_empty" && operator !== "is_not_empty";
}

interface FiltersPanelProps<T> {
  open: boolean;
  onClose: () => void;
  columns: Column<T>[];
  filters: TableFilter[];
  onChange: (filters: TableFilter[]) => void;
}

export function DataTableFiltersPanel<T>({ open, onClose, columns, filters, onChange }: FiltersPanelProps<T>) {
  const filterableColumns = useMemo(
    () => columns.filter((col) => col.filterable !== false && col.key !== "actions" && hasFilterValueSource(col)),
    [columns],
  );

  const addFilter = () => {
    const first = filterableColumns[0];
    if (!first) return;
    const type = inferColumnFilterType(first);
    const ops = operatorsForType(type);
    onChange([
      ...filters,
      { id: filterId(), columnKey: first.key, operator: ops[0]!.value, value: "" },
    ]);
  };

  const updateFilter = (id: string, patch: Partial<TableFilter>) => {
    onChange(filters.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  };

  const removeFilter = (id: string) => {
    onChange(filters.filter((f) => f.id !== id));
  };

  return (
    <RightSidebarPanel open={open} title="Filters" onClose={onClose}>
      <div className="space-y-3">
        {filters.length === 0 && (
          <p className="text-xs text-muted">No filters applied. Add a filter to narrow this table.</p>
        )}

        {filters.map((filter) => {
          const col = filterableColumns.find((c) => c.key === filter.columnKey) ?? filterableColumns[0];
          if (!col) return null;
          const type = inferColumnFilterType(col);
          const ops = operatorsForType(type);

          return (
            <div key={filter.id} className="space-y-2 rounded-xl border border-line bg-hover p-3">
              <SearchSelect
                label="Column"
                options={filterableColumns.map((c) => ({
                  value: c.key,
                  label: c.header || c.key,
                }))}
                value={filter.columnKey}
                onChange={(columnKey) => {
                  const nextCol = filterableColumns.find((c) => c.key === columnKey);
                  const nextType = nextCol ? inferColumnFilterType(nextCol) : "string";
                  const nextOps = operatorsForType(nextType);
                  updateFilter(filter.id, {
                    columnKey,
                    operator: nextOps[0]!.value,
                    value: "",
                  });
                }}
              />

              <SearchSelect
                label="Condition"
                options={ops.map((op) => ({ value: op.value, label: op.label }))}
                value={filter.operator}
                onChange={(operator) => updateFilter(filter.id, { operator: operator as FilterOperator })}
              />

              {needsValue(filter.operator) && (
                <>
                  {type === "boolean" ? (
                    <SearchSelect
                      label="Value"
                      options={[
                        { value: "true", label: "True" },
                        { value: "false", label: "False" },
                      ]}
                      value={filter.value}
                      onChange={(value) => updateFilter(filter.id, { value })}
                    />
                  ) : type === "select" && col.filterOptions?.length ? (
                    <SearchSelect
                      label="Value"
                      placeholder="Select…"
                      options={[
                        { value: "", label: "Select…" },
                        ...col.filterOptions.map((opt) => ({ value: opt.value, label: opt.label })),
                      ]}
                      value={filter.value}
                      onChange={(value) => updateFilter(filter.id, { value })}
                    />
                  ) : (
                    <label className="block">
                      <span className="mb-1 block text-xs font-medium text-muted">Value</span>
                      <input
                        type={type === "number" ? "number" : "text"}
                        value={filter.value}
                        onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
                        className="w-full rounded-xl border border-line bg-input-bg px-3 py-2 text-sm text-foreground outline-none focus:border-brand-pink/60"
                      />
                    </label>
                  )}
                </>
              )}

              <div className="flex justify-end">
                <Button variant="ghost" size="sm" onClick={() => removeFilter(filter.id)}>
                  Remove
                </Button>
              </div>
            </div>
          );
        })}

        <button
          type="button"
          onClick={addFilter}
          disabled={filterableColumns.length === 0}
          className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-line py-2.5 text-sm text-muted transition-colors hover:border-brand-pink/35 hover:bg-hover hover:text-foreground disabled:opacity-40"
        >
          <Plus size={16} />
          Add Filter
        </button>
      </div>
    </RightSidebarPanel>
  );
}

interface ColumnsPanelProps<T> {
  open: boolean;
  onClose: () => void;
  columns: Column<T>[];
  columnOrder: string[];
  columnVisible: Record<string, boolean>;
  onOrderChange: (order: string[]) => void;
  onVisibilityChange: (visible: Record<string, boolean>) => void;
}

export function DataTableColumnsPanel<T>({
  open,
  onClose,
  columns,
  columnOrder,
  columnVisible,
  onOrderChange,
  onVisibilityChange,
}: ColumnsPanelProps<T>) {
  const [dragKey, setDragKey] = useState<string | null>(null);

  const ordered = useMemo(() => {
    const map = new Map(columns.map((c) => [c.key, c]));
    return columnOrder.map((key) => map.get(key)).filter(Boolean) as Column<T>[];
  }, [columns, columnOrder]);

  const hideable = ordered.filter((c) => c.hideable !== false);
  const visibleCount = hideable.filter((c) => columnVisible[c.key] !== false).length;

  const showAll = () => {
    const next = { ...columnVisible };
    for (const col of hideable) next[col.key] = true;
    onVisibilityChange(next);
  };

  const hideAll = () => {
    const next = { ...columnVisible };
    for (const col of hideable) next[col.key] = false;
    onVisibilityChange(next);
  };

  const toggleVisible = (key: string) => {
    onVisibilityChange({ ...columnVisible, [key]: columnVisible[key] === false });
  };

  const onDragStart = (key: string) => (event: DragEvent) => {
    setDragKey(key);
    event.dataTransfer.effectAllowed = "move";
  };

  const onDragOver = (key: string) => (event: DragEvent) => {
    event.preventDefault();
    if (!dragKey || dragKey === key) return;
    const from = columnOrder.indexOf(dragKey);
    const to = columnOrder.indexOf(key);
    if (from < 0 || to < 0) return;
    const next = [...columnOrder];
    next.splice(from, 1);
    next.splice(to, 0, dragKey);
    onOrderChange(next);
  };

  const onDragEnd = () => setDragKey(null);

  return (
    <RightSidebarPanel open={open} title="Columns" onClose={onClose}>
      <div className="space-y-3">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted">
            {visibleCount} of {hideable.length} visible
          </span>
          <span className="flex gap-2">
            <button type="button" onClick={showAll} className="text-brand-pink hover:underline">
              Show all
            </button>
            <button type="button" onClick={hideAll} className="text-brand-pink hover:underline">
              Hide all
            </button>
          </span>
        </div>

        <ul className="space-y-1">
          {ordered.map((col) => {
            const canHide = col.hideable !== false && col.key !== "actions";
            const visible = columnVisible[col.key] !== false;
            const type = inferColumnFilterType(col);

            return (
              <li
                key={col.key}
                draggable
                onDragStart={onDragStart(col.key)}
                onDragOver={onDragOver(col.key)}
                onDragEnd={onDragEnd}
                className={`flex items-center gap-2 rounded-lg border border-transparent px-2 py-2 transition-colors ${
                  dragKey === col.key ? "border-brand-pink/30 bg-brand-pink/5" : "hover:bg-hover"
                }`}
              >
                <GripVertical size={14} className="shrink-0 cursor-grab text-muted-2 active:cursor-grabbing" />
                <span className="min-w-0 flex-1 truncate text-sm text-foreground">{col.header || col.key}</span>
                <span className="shrink-0 rounded-md bg-hover px-1.5 py-0.5 text-[10px] text-muted-2">{type}</span>
                {canHide ? (
                  <button
                    type="button"
                    aria-label={visible ? `Hide ${col.header}` : `Show ${col.header}`}
                    onClick={() => toggleVisible(col.key)}
                    className="shrink-0 rounded p-1 text-muted hover:bg-hover hover:text-foreground"
                  >
                    {visible ? <Eye size={16} /> : <EyeOff size={16} className="opacity-50" />}
                  </button>
                ) : (
                  <span className="w-7" />
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </RightSidebarPanel>
  );
}

export function DataTableMoreMenu({
  open,
  onClose,
  anchorRef,
  onRefresh,
  onClearFilters,
  hasFilters,
}: {
  open: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  onRefresh?: () => void;
  onClearFilters: () => void;
  hasFilters: boolean;
}) {
  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-[70]" onClick={onClose} aria-hidden="true" />
      <div
        className="absolute right-0 top-[calc(100%+4px)] z-[75] min-w-[160px] overflow-hidden rounded-xl border border-line bg-surface py-1 shadow-xl"
        style={
          anchorRef.current
            ? undefined
            : undefined
        }
      >
        {onRefresh && (
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground hover:bg-hover"
            onClick={() => {
              onRefresh();
              onClose();
            }}
          >
            <RefreshCw size={14} className="text-muted" />
            Refresh
          </button>
        )}
        <button
          type="button"
          disabled={!hasFilters}
          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground hover:bg-hover disabled:opacity-40"
          onClick={() => {
            onClearFilters();
            onClose();
          }}
        >
          Clear Filters
        </button>
      </div>
    </>
  );
}

function hasFilterValueSource<T>(col: Column<T>): boolean {
  return Boolean(col.sortValue ?? col.exportValue ?? col.searchValue);
}

export function inferColumnFilterType<T>(col: Column<T>): ColumnFilterType {
  if (col.filterType) return col.filterType;
  if (col.filterOptions?.length) return "select";
  if (/date|created|updated|_at$/i.test(col.key) || /date|time/i.test(col.header)) return "datetime";
  if (/count|qty|quantity|order|amount|price|stock|fields/i.test(col.key)) return "number";
  if (col.key === "active" || /^is[A-Z]/.test(col.key)) return "boolean";
  return "string";
}

export function getColumnRawValue<T>(
  col: Column<T>,
  row: T,
): string | number | boolean | null | undefined {
  return col.sortValue?.(row) ?? col.exportValue?.(row) ?? col.searchValue?.(row);
}

export function rowMatchesFilter<T>(row: T, col: Column<T>, filter: TableFilter): boolean {
  const raw = getColumnRawValue(col, row);
  const str = raw == null ? "" : String(raw);
  const type = inferColumnFilterType(col);

  switch (filter.operator) {
    case "is_empty":
      return raw == null || str === "";
    case "is_not_empty":
      return raw != null && str !== "";
    case "contains":
      return str.toLowerCase().includes(filter.value.toLowerCase());
    case "not_equals":
      if (type === "number") return Number(raw) !== Number(filter.value);
      return str.toLowerCase() !== filter.value.toLowerCase();
    case "equals":
      if (type === "boolean") return String(raw) === filter.value;
      if (type === "number") return Number(raw) === Number(filter.value);
      return str.toLowerCase() === filter.value.toLowerCase();
    case "gt":
      if (type === "number") return Number(raw) > Number(filter.value);
      return str.localeCompare(filter.value) > 0;
    case "lt":
      if (type === "number") return Number(raw) < Number(filter.value);
      return str.localeCompare(filter.value) < 0;
    default:
      return true;
  }
}
